<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content" lang="de">
        <section class="intro">
          <p>
            Eine Google Street View-Tour für Ihr Unternehmen zu erstellen, ist
            die kostengünstigste Möglichkeit, Ihre Präsenz in der Google-Suche
            zu stärken. Wenn Nutzer Ihr Unternehmen in Google Maps oder der
            Google-Suche finden, können sie nun Ihr Innenleben erkunden – was zu
            doppelt so hohem Engagement führt wie statische Fotos allein. Dieser
            Leitfaden zeigt Ihnen genau, wie es geht.
          </p>
          <p>
            Die Bilder liegen an <em>Ihrem</em> Standort auf Google Maps.
            <strong>Travvir ist der Street-View-Publisher</strong> an diesem Ort
            — ohne Anmeldung mit Ihrem privaten Google-Konto.
          </p>
          <p>
            Ein <strong>einzelner Scan</strong>: Ort auf Maps wählen, Szenen
            auswählen, veröffentlichen. Eine <strong>360°-Tour</strong>: Ort
            wählen und die Tour direkt veröffentlichen. Produktdetails auf der
            <a :href="`${mainSiteUrl}/google-streetview`"
              >Google-Street-View-Seite</a
            >; die App-Schritte unter
            <a href="/blog/de/google-street-view"
              >Veröffentlichen mit Travvir</a
            >.
          </p>
        </section>

        <section>
          <h2>Was eine Google Street View-Tour ist</h2>
          <p>
            Eine Google Street View-Tour für Unternehmen ist eine Sammlung
            verknüpfter 360°-Fotos, die in Google Maps, der Google-Suche und dem
            Google Business-Profil Ihres Unternehmens erscheinen. Nutzer sehen
            die Schaltfläche „Innen ansehen" in Ihrem Eintrag und können
            virtuell durch Ihre Räumlichkeiten navigieren.
          </p>
          <p>
            Dies unterscheidet sich von der Street View-Außenansicht, die
            Googles eigene Fahrzeuge und Trekker aufnehmen – Innen-Touren werden
            von Unternehmen selbst oder von Google Trusted Photographers
            erstellt und hochgeladen.
          </p>
        </section>

        <section>
          <h2>SEO-Vorteile</h2>
          <p>
            Google Business-Profile mit virtuellen Touren erhalten im
            Durchschnitt
            <strong>doppelt so viel Engagement</strong> wie Profile ohne Tour.
            „Engagement" umfasst Klicks auf Wegbeschreibungen, Website-Klicks,
            Anruf-Klicks und Fotoaufrufe – alles Signale, die die lokale SEO
            stärken.
          </p>
          <p>
            Der Mechanismus: Mehr Engagement → stärkere lokale SEO-Signale →
            höhere Positionen in lokalen Suchergebnissen → mehr Sichtbarkeit →
            mehr Kunden. Eine virtuelle Tour schlägt sich direkt im Umsatz
            nieder.
          </p>
        </section>

        <section>
          <h2>Wie Sie Ihre Google Street View-Tour erstellen</h2>

          <h3>Option 1: Travvir + Upload (empfohlen)</h3>
          <p>
            Dieser Weg produziert die besten Ergebnisse und ist am schnellsten:
          </p>
          <p>
            <strong>Schritt 1:</strong> Laden Sie die Travvir-App herunter und
            erstellen Sie Ihre virtuelle Tour (jedes Zimmer oder jeden Bereich
            als separate Szene aufnehmen). Der KI-Verarbeitungsalgorithmus von
            Travvir erzeugt hochwertige equirektanguläre Bilder.
          </p>
          <p>
            <strong>Schritt 2:</strong> In der Travvir-App „Teilen“ → „Google
            Street View“.
          </p>
          <p>
            <strong>Schritt 3:</strong> Den Google-Maps-Ort wählen. Beim
            einzelnen Scan Szenen auswählen und veröffentlichen; bei einer
            360°-Tour die Tour direkt veröffentlichen. Travvir ist der Publisher
            an diesem Ort.
          </p>
          <p>
            <strong>Schritt 4:</strong> Google verarbeitet die Fotos (dauert 1–3
            Stunden). Danach erscheinen sie in Ihrem Profil unter „Innen
            ansehen".
          </p>

          <h3>Option 2: Direkt per Street View App</h3>
          <p>
            Laden Sie die Google Street View App herunter. Tippen Sie auf
            „Erstellen" → „Verbundene 360°-Fotos aufnehmen". Laden Sie jede
            Szene einzeln hoch. Ziehen Sie sie auf der Karte an die korrekte
            Position. Veröffentlichen Sie. Der Prozess ist zeitaufwendiger als
            der Travvir-Export, aber komplett kostenlos ohne externe App.
          </p>
        </section>

        <section>
          <h2>Best Practice für optimale Ergebnisse</h2>

          <h3>Aufnahme-Best-Practices</h3>
          <p>
            Fotografieren Sie an Wochentagen früh am Morgen, damit keine Kunden
            oder Mitarbeiter im Bild sind. Schalten Sie alle Lichter ein und
            sorgen Sie für gleichmäßige Beleuchtung. Beginnen Sie mit dem
            Eingangsbereich – das ist die erste Szene, die Betrachter sehen.
            Fotografieren Sie jeden wichtigen Bereich, den Kunden sehen würden,
            wenn sie Ihr Unternehmen besuchen.
          </p>

          <h3>Qualitäts-Best-Practices</h3>
          <p>
            Verwenden Sie ein Stativ für scharfe, gerade Bilder. Halten Sie
            mindestens 10 Szenen bereit – mehr ist besser. Stellen Sie sicher,
            dass benachbarte Szenen räumlich miteinander verbunden sind (d. h.
            man soll von einem Raum in den nächsten spazieren können). Prüfen
            Sie die Fotos auf Ihre Korrektheit, bevor Sie sie veröffentlichen.
          </p>
        </section>

        <section>
          <h2>Fazit</h2>
          <p>
            Eine Google Street View-Tour für Ihr Unternehmen zu erstellen, ist
            eine der wirkungsvollsten und kosteneffizientesten lokalen
            Marketingmaßnahmen, die Sie durchführen können. Die Tour erscheint
            genau dort, wo Kunden nach Ihnen suchen – in Google – und liefert
            die visuelle Überzeugung, die Betrachter zum Besucher und
            schließlich zum Kunden macht.
          </p>
        </section>
      
        <section>
          <h2>Weiterlesen</h2>
          <ul>
            <li>
              <a :href="`${mainSiteUrl}/google-streetview`"
                >Google Street View mit Travvir</a
              >
            </li>
            <li>
              <a href="/blog/de/google-street-view"
                >Scan oder Tour mit Travvir veröffentlichen</a
              >
            </li>
            <li>
              <a href="/blog/de/google-business-virtual-tour"
                >Google Business Virtual Tour</a
              >
            </li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;
const mainSiteUrl = config.public.baseUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "google-street-view-virtual-tour",
);

useBlogSeo({
  title: "Google Street View Virtual Tour [2026]: Schritt für Schritt | Travvir",
  description:
    "Google Street View Virtual Tour: Ort auf Maps wählen, dann einzelnen Scan (Szenen) oder 360°-Tour direkt veröffentlichen. Publisher ist Travvir an diesem Standort.",
  slug: "google-street-view-virtual-tour",
  image: postData?.image,
  postData,
  lang: "de",
});

</script>
